/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import { FINDER_FILE_RENAME, APPLICATION_SET_ACTIVE_EDITOR, TAB_CLOSE, TAB_OPEN, TAB_MOVE, TAB_UPDATE, TAB_REPLACE, TABS_FETCH, TABS_CLEAR, TABS_CLOSE, TAB_REDIRECT_ADD, TABS_REDIRECTS_CLEAR, TOGGLE_TABS_SCROLL_INTO_VIEW } from 'DesignData/actions/ActionTypes';
import SeamlessImmutable from 'seamless-immutable';
import { combineReducers } from 'redux';
import Tab from 'DesignData/models/Tab';
import { NONE } from 'DesignData/enums/DesignAssetTypes';
import { CUSTOM_WIDGET, RAW_ASSET } from 'DesignData/enums/FolderAssetTypes';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { EMPTY_ARRAY } from 'DesignData/lib/array';

const uniq = arr => Array.from(new Set(arr));
/**
 * @param {Object}                            tabs
 * @param {DesignData/enums/DesignAssetTypes} assetType
 * @param {Object}                            newData
 * @return {Object}
 */


function updateTab(tabs, tabId, data) {
  const existingTab = tabs[tabId];

  if (!(existingTab && data)) {
    return tabs;
  }

  return tabs.set(tabId, existingTab.merge(data));
}

const EMPTY_TABS = EMPTY_OBJECT;
/*
 * tabs
 */

function byId(state = EMPTY_TABS, action) {
  switch (action.type) {
    case TABS_FETCH:
      {
        const {
          tabs
        } = action;

        if (!(tabs && tabs.byId)) {
          return state;
        }

        let newState = state;
        Object.keys(tabs.byId || {}).forEach(tabId => {
          const tabData = tabs.byId[tabId];
          const tab = new Tab(tabData);
          newState = newState.set(tab.id, tab);
        });
        return newState;
      }

    case TAB_CLOSE:
      {
        const existingTab = state[action.tab.id];

        if (!existingTab) {
          return state;
        }

        return state.without(existingTab.id);
      }

    case TAB_OPEN:
      {
        const {
          browser,
          tab
        } = action;

        if (browser || !tab) {
          return state;
        }

        const existingTab = state[tab.id];

        if (existingTab === tab) {
          return state;
        }

        return SeamlessImmutable.set(state, tab.id, tab.set('lastActiveAt', Date.now()), {
          deep: !!existingTab
        });
      }

    case TABS_CLEAR:
      {
        return EMPTY_TABS;
      }

    case TABS_CLOSE:
      {
        return action.tabs.reduce((updatedState, tab) => {
          const existingTab = updatedState[tab.id];
          if (!existingTab) return updatedState;
          return updatedState.without(tab.id);
        }, state);
      }

    case TAB_UPDATE:
      {
        const {
          tab
        } = action;
        if (!tab) return state;
        return updateTab(state, tab.id, action.data);
      }

    case TAB_REPLACE:
      {
        const {
          tab,
          newTab
        } = action;
        state = SeamlessImmutable.without(state, tab.id);
        state = SeamlessImmutable.set(state, newTab.id, newTab);
        return state;
      }

    case FINDER_FILE_RENAME:
      {
        const {
          fileType,
          file,
          fileId
        } = action; //  Custom Widget and Raw Asset have renaming implemented differently
        //  Background: https://git.hubteam.com/HubSpot/DesignManagerUI/pull/4826

        if (![CUSTOM_WIDGET, RAW_ASSET].includes(fileType) || !file || !fileId) {
          return state;
        }

        const tab = state[fileId];

        if (!tab) {
          //  If rename occured but no tab is currently loaded for it
          return state;
        }

        return updateTab(state, tab.id, {
          label: file.label
        });
      }

    default:
      return state;
  }
}

function order(state = EMPTY_ARRAY, action) {
  switch (action.type) {
    case TABS_FETCH:
      {
        const {
          tabs
        } = action;

        if (!(tabs && tabs.byId)) {
          return state;
        }

        return SeamlessImmutable(uniq(tabs.order));
      }

    case TAB_CLOSE:
      {
        return state.filter(tabId => tabId !== action.tab.id);
      }

    case TAB_OPEN:
      {
        const {
          browser,
          tab
        } = action; // Open browser

        if (browser || state.indexOf(tab.id) > -1) {
          return state;
        }
        /* welcome tab always comes first */


        if (tab.id === 'welcome') {
          return SeamlessImmutable(['welcome', ...state]);
        } else if (tab.id === 'gettingStarted') {
          return SeamlessImmutable(['gettingStarted', ...state]);
        } // Add new tab


        return state.concat([tab.id]);
      }

    case TAB_MOVE:
      {
        const {
          tabId,
          newIndex
        } = action;

        if (state.indexOf(tabId) === -1) {
          return state;
        }

        const withoutTabId = state.filter(id => id !== tabId);
        const upToNewIndex = withoutTabId.slice(0, newIndex);
        return upToNewIndex.concat([tabId]).concat(withoutTabId.slice(newIndex));
      }

    case TAB_REPLACE:
      {
        const {
          tab,
          newTab
        } = action;
        if (!state.includes(tab.id)) return state;
        const existingIdx = state.indexOf(newTab.id);

        if (existingIdx >= 0) {
          state = [...state.slice(0, existingIdx), ...state.slice(existingIdx + 1)];
        }

        const targetIdx = state.indexOf(tab.id);
        state = [...state.slice(0, targetIdx), newTab.id, ...state.slice(targetIdx + 1)];
        return SeamlessImmutable(uniq(state));
      }

    case TABS_CLEAR:
      {
        return EMPTY_ARRAY;
      }

    case TABS_CLOSE:
      {
        const tabIds = action.tabs.map(tab => tab.id);
        return state.filter(tabId => !~tabIds.indexOf(tabId));
      }

    default:
      return state;
  }
}
/*
 * activeTabId
 */


function activeTabId(state = null, action) {
  switch (action.type) {
    case APPLICATION_SET_ACTIVE_EDITOR:
      {
        const {
          assetType,
          assetId
        } = action;

        if (assetType === NONE) {
          return null;
        }

        return assetId;
      }

    case TAB_REPLACE:
      {
        const {
          tab,
          newTab
        } = action; // Tab being replaced is active, set active to the new tab.

        if (state === tab.id) {
          return newTab.id;
        }

        return state;
      }

    default:
      return state;
  }
}
/*
 * lastOpen
 *
 * This will usually be the same as activeTabId
 * It is used by the router to open the last active tab
 * for non-tab routes. E.g. the user linked to Design Manager
 * from somewhere else.
 */


function lastOpen(state = null, action) {
  switch (action.type) {
    case TABS_FETCH:
      {
        const {
          tabs
        } = action;
        if (!tabs) return state;
        return tabs.lastOpen || state;
      }

    case APPLICATION_SET_ACTIVE_EDITOR:
      {
        return action.assetId || state;
      }

    case TABS_CLEAR:
      {
        return null;
      }

    default:
      return state;
  }
}
/**
 * Should scroll into view
 */


function shouldScrollActiveTabIntoView(state = false, action) {
  switch (action.type) {
    case TOGGLE_TABS_SCROLL_INTO_VIEW:
      {
        return !state;
      }

    default:
      return state;
  }
}

const redirects = (state = EMPTY_OBJECT, action) => {
  switch (action.type) {
    case TAB_REDIRECT_ADD:
      {
        const {
          from,
          to
        } = action;
        if (!from || !to) return state;
        state = SeamlessImmutable.set(state, from, to);
        return state;
      }

    case TABS_REDIRECTS_CLEAR:
      {
        return EMPTY_OBJECT;
      }

    default:
      return state;
  }
};
/*
 * Export
 */


export default combineReducers({
  byId,
  order,
  activeTabId,
  lastOpen,
  shouldScrollActiveTabIntoView,
  redirects
});